/** Shared admin filters for unmatched bookings and manual matching. */

export const SERVICE_SCOPE = {
  NEXT_SERVICE: 'next_service',
  UPCOMING: 'upcoming',
  ALL: 'all',
};

const SESSION_RANK = {
  '1st': 1,
  '2nd': 2,
  '3rd': 3,
  midweek: 4,
};

export function parseBookingDate(value) {
  if (!value) return null;
  const iso = String(value).slice(0, 10);
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function startOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export function normalizeDateKey(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

export function isUnmatchedBooking(booking) {
  const status = String(booking?.status || '').toLowerCase();
  const ride = booking?.ride || booking?.Ride || null;
  const hasDriver = Boolean(
    booking?.driverId ||
    booking?.driver?.id ||
    ride?.driverId ||
    ride?.driver?.id
  );
  return status === 'pending' && !hasDriver;
}

function sessionRank(session) {
  if (!session) return 99;
  return SESSION_RANK[String(session).toLowerCase()] ?? SESSION_RANK[session] ?? 50;
}

/** Sort key: soonest date, then 1st → 2nd → 3rd → midweek. */
export function compareServiceSlots(a, b) {
  const dateA = parseBookingDate(a.date);
  const dateB = parseBookingDate(b.date);
  if (dateA && dateB && dateA.getTime() !== dateB.getTime()) {
    return dateA.getTime() - dateB.getTime();
  }
  if (!dateA && dateB) return 1;
  if (dateA && !dateB) return -1;
  return sessionRank(a.serviceSession) - sessionRank(b.serviceSession);
}

/**
 * Earliest upcoming unmatched slot (date + service session on that booking).
 */
export function getNextServiceSlot(bookings) {
  const today = startOfToday();
  const upcoming = bookings.filter((booking) => {
    const date = parseBookingDate(booking.date);
    return date && date >= today;
  });
  if (upcoming.length === 0) return null;

  const sorted = [...upcoming].sort(compareServiceSlots);
  const first = sorted[0];
  return {
    date: normalizeDateKey(first.date),
    serviceSession: first.serviceSession || null,
    serviceTime: first.serviceTime || null,
  };
}

export function bookingMatchesServiceSlot(booking, slot) {
  if (!slot) return false;
  if (normalizeDateKey(booking.date) !== slot.date) return false;
  if (slot.serviceSession && booking.serviceSession) {
    return booking.serviceSession === slot.serviceSession;
  }
  return true;
}

export function filterUnmatchedByScope(unmatched, scope, nextSlot) {
  if (scope === SERVICE_SCOPE.ALL) return unmatched;

  const today = startOfToday();
  if (scope === SERVICE_SCOPE.UPCOMING) {
    return unmatched.filter((booking) => {
      const date = parseBookingDate(booking.date);
      return date && date >= today;
    });
  }

  if (scope === SERVICE_SCOPE.NEXT_SERVICE) {
    if (!nextSlot) return [];
    return unmatched.filter((booking) => bookingMatchesServiceSlot(booking, nextSlot));
  }

  return unmatched;
}

export function formatServiceSlotLabel(slot) {
  if (!slot?.date) return 'No upcoming service';
  const date = parseBookingDate(slot.date);
  const dateLabel = date
    ? date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : slot.date;
  const session = slot.serviceSession ? `${slot.serviceSession} service` : 'service';
  const time = slot.serviceTime ? ` · ${slot.serviceTime}` : '';
  return `${dateLabel} · ${session}${time}`;
}

export function rideCompatibleWithBooking(ride, booking) {
  if (!ride || !booking) return false;
  if (normalizeDateKey(ride.date) !== normalizeDateKey(booking.date)) return false;
  if (
    booking.serviceSession &&
    ride.serviceSession &&
    booking.serviceSession !== ride.serviceSession
  ) {
    return false;
  }
  const seatsNeeded = Number(booking.seats || booking.passengers || 1);
  return Number(ride.availableSeats) >= seatsNeeded &&
    String(ride.status || '').toLowerCase() === 'scheduled';
}
